/**
 * Adapter between the matrix-based UI template (clt.mock.ts) and the flat
 * rule-list shape the backend uses. The wizard keeps its 4-substep matrices;
 * we flatten on write and re-bucket on read.
 *
 * Lossy fields (UI does not surface these yet — TODO when the wizard learns
 * about them):
 *  - rule.regime          → defaults to template-level `_regime` passthrough
 */
import {
  cellKey,
  makeBiweeklyGroupId,
  makeBiweeklyTargetId,
  type ApplyTarget,
  type BiweeklyOffsetGroup,
  type BiweeklyTarget,
  type CalendarLogicTemplate,
  type CltMatrix,
  type StrategyGroup,
  type SubstepKey,
  type ValueChooserRule,
} from '@/components/CalendarLogicTemplates/clt.mock'
import type {
  ApplyScope,
  ApplyTargetRead,
  ApplyTargetWrite,
  BiweeklyGroupRead,
  BiweeklyGroupWrite,
  CalendarLogicTemplateRead,
  CalendarLogicTemplateSummaryRead,
  CalendarLogicTemplateWrite,
  Regime,
  StrategyGroupRead,
  StrategyGroupWrite,
  TemplateRuleRead,
  TemplateRuleWrite,
} from './clt.types'

/** UI template extended with a regime passthrough field. */
export interface UiCalendarLogicTemplate extends CalendarLogicTemplate {
  /** Round-tripped from backend; UI does not edit this yet. */
  _regime?: Regime
}

/** Lightweight projection used by the list view. */
export interface UiCalendarLogicTemplateSummary {
  id: string
  name: string
  description?: string
  appliesToWeeks: number[]
  isDefault: boolean
  ruleCount: number
  strategyGroupCount: number
  biweeklyGroupCount: number
  applyTargetCount: number
  updatedAt: string
  updatedBy: string
}

export function fromApiTemplateSummary(
  api: CalendarLogicTemplateSummaryRead
): UiCalendarLogicTemplateSummary {
  return {
    id: api.id,
    name: api.name,
    description: api.description ?? undefined,
    appliesToWeeks: api.applies_to_weeks,
    isDefault: api.is_default,
    ruleCount: api.rule_count,
    strategyGroupCount: api.strategy_group_count,
    biweeklyGroupCount: api.biweekly_group_count,
    applyTargetCount: api.apply_target_count,
    updatedAt: api.updated_at,
    updatedBy: api.updated_by,
  }
}

const SUBSTEPS: SubstepKey[] = [
  'IMPORT_RAIL',
  'IMPORT_NO_RAIL',
  'DOMESTIC_RAIL',
  'DOMESTIC_NO_RAIL',
]

function substepFor(isImport: boolean, isRail: boolean): SubstepKey {
  if (isImport) return isRail ? 'IMPORT_RAIL' : 'IMPORT_NO_RAIL'
  return isRail ? 'DOMESTIC_RAIL' : 'DOMESTIC_NO_RAIL'
}

function substepFlags(substep: SubstepKey): {
  isImport: boolean
  isRail: boolean
} {
  return {
    isImport: substep.startsWith('IMPORT'),
    isRail: !substep.endsWith('NO_RAIL'),
  }
}

function ruleToWire(
  substep: SubstepKey,
  milestoneId: string,
  strategyGroupId: string,
  rule: ValueChooserRule,
  regime: Regime
): TemplateRuleWrite {
  const { isImport, isRail } = substepFlags(substep)
  return {
    regime,
    is_import: isImport,
    is_rail: isRail,
    milestone_id: milestoneId,
    strategy_group_id: strategyGroupId,
    mode: rule.mode,
    vs_milestone_id: rule.fromMilestoneId ?? null,
    timing: rule.deltaDays ?? 0,
    dynamic: rule.dynamic,
    biweekly_adj: 0,
    calendar_column: rule.calendarColumn ?? null,
  }
}

function strategyGroupToWire(g: StrategyGroup): StrategyGroupWrite {
  return { id: g.id, label: g.label, codes: g.codes }
}

/**
 * One UI biweekly card expands into N wire rows — one per target — sharing the
 * card's label / offset_days / milestone_ids. Targets with an empty strategy
 * group id are skipped (validation should prevent this from reaching the wire).
 */
function biweeklyOffsetGroupToWires(
  g: BiweeklyOffsetGroup
): BiweeklyGroupWrite[] {
  return g.targets
    .filter(t => t.strategyGroupId !== '')
    .map(t => ({
      label: g.label,
      offset_days: g.offsetDays,
      milestone_ids: g.milestoneIds,
      strategy_group_id: t.strategyGroupId,
      is_import: t.isImport,
      is_rail: t.isRail,
    }))
}

function biweeklyTargetFromRead(g: BiweeklyGroupRead): BiweeklyTarget {
  return {
    id: makeBiweeklyTargetId(),
    strategyGroupId: g.strategy_group_id,
    isImport: g.is_import,
    isRail: g.is_rail,
  }
}

/**
 * Collapse the flat backend rows into UI cards by grouping on
 * (label, offset_days, sorted milestone_ids). Each row inside a group becomes
 * one target. Round-trips the "one card, multiple targets" UI model.
 */
function biweeklyOffsetGroupsFromReads(
  rows: BiweeklyGroupRead[]
): BiweeklyOffsetGroup[] {
  const buckets = new Map<string, BiweeklyOffsetGroup>()
  for (const row of rows) {
    const key = JSON.stringify([
      row.label,
      row.offset_days,
      [...row.milestone_ids].sort(),
    ])
    const existing = buckets.get(key)
    if (existing) {
      existing.targets.push(biweeklyTargetFromRead(row))
    } else {
      buckets.set(key, {
        id: makeBiweeklyGroupId(),
        label: row.label,
        offsetDays: row.offset_days,
        milestoneIds: [...row.milestone_ids],
        targets: [biweeklyTargetFromRead(row)],
      })
    }
  }
  return Array.from(buckets.values())
}

function applyTargetToWire(t: ApplyTarget): ApplyTargetWrite {
  return {
    calendar_id: t.calendarId,
    scope: t.scope as ApplyScope,
    event_ids: t.scope === 'SPECIFIC' ? t.eventIds : [],
  }
}

/**
 * Build the create/update payload from a UI template. Caller does not need to
 * include the id; the API path already names it for updates.
 */
export function toApiTemplate(
  ui: UiCalendarLogicTemplate
): CalendarLogicTemplateWrite {
  // TODO: surface `regime` in the UI. For now we round-trip the value the
  // backend gave us (_regime) and default to 'starting' on first create.
  const regime: Regime = ui._regime ?? 'starting'

  const rules: TemplateRuleWrite[] = []
  for (const substep of SUBSTEPS) {
    const matrix: CltMatrix = ui.matrices[substep] ?? {}
    for (const [key, rule] of Object.entries(matrix)) {
      const [milestoneId, strategyGroupId] = key.split('__')
      if (!milestoneId || !strategyGroupId) continue
      rules.push(
        ruleToWire(substep, milestoneId, strategyGroupId, rule, regime)
      )
    }
  }

  return {
    name: ui.name,
    description: ui.description ?? null,
    // Legacy template-level offset — meaningful offsets now live in
    // `biweekly_groups`. Kept on the wire for back-compat; always 0.
    biweekly_offset: 0,
    applies_to_weeks: ui.appliesToWeeks,
    strategy_groups: ui.strategyGroups.map(strategyGroupToWire),
    rules,
    biweekly_groups: ui.biweeklyOffsetGroups.flatMap(
      biweeklyOffsetGroupToWires
    ),
    apply_targets: ui.applyTargets.map(applyTargetToWire),
    is_default: ui.isDefault,
  }
}

function strategyGroupFromRead(g: StrategyGroupRead): StrategyGroup {
  return { id: g.id, label: g.label, codes: g.codes }
}

function applyTargetFromRead(t: ApplyTargetRead): ApplyTarget {
  return {
    calendarId: t.calendar_id,
    scope: t.scope,
    eventIds: t.event_ids ?? [],
  }
}

function ruleFromRead(rule: TemplateRuleRead): {
  substep: SubstepKey
  key: string
  rule: ValueChooserRule
} {
  const substep = substepFor(rule.is_import, rule.is_rail)
  const ui: ValueChooserRule = {
    mode: rule.mode,
    // Older responses may omit the coefficient; do not infer it from the mode.
    dynamic: rule.dynamic ?? 0,
    fromMilestoneId: rule.vs_milestone_id ?? undefined,
    deltaDays: rule.timing ?? 0,
    calendarColumn: rule.calendar_column ?? undefined,
  }
  return {
    substep,
    key: cellKey(rule.milestone_id, rule.strategy_group_id),
    rule: ui,
  }
}

/** Build the UI template from the backend read shape. */
export function fromApiTemplate(
  api: CalendarLogicTemplateRead
): UiCalendarLogicTemplate {
  const matrices: Record<SubstepKey, CltMatrix> = {
    IMPORT_RAIL: {},
    IMPORT_NO_RAIL: {},
    DOMESTIC_RAIL: {},
    DOMESTIC_NO_RAIL: {},
  }
  for (const rule of api.rules) {
    const mapped = ruleFromRead(rule)
    matrices[mapped.substep][mapped.key] = mapped.rule
  }

  // Round-trip regime: pick the most common per-rule value (templates are
  // typically all 'prior' or all 'starting' today). Default to 'starting' if
  // there are no rules yet.
  const regimeCounts: Record<Regime, number> = { prior: 0, starting: 0 }
  for (const rule of api.rules) regimeCounts[rule.regime]++
  const _regime: Regime =
    regimeCounts.prior > regimeCounts.starting ? 'prior' : 'starting'

  return {
    id: api.id,
    name: api.name,
    description: api.description ?? undefined,
    strategyGroups: api.strategy_groups.map(strategyGroupFromRead),
    matrices,
    biweeklyOffset: api.biweekly_offset,
    biweeklyOffsetGroups: biweeklyOffsetGroupsFromReads(
      api.biweekly_groups ?? []
    ),
    appliesToWeeks: api.applies_to_weeks,
    applyTargets: api.apply_targets.map(applyTargetFromRead),
    isDefault: api.is_default,
    updatedAt: api.updated_at,
    _regime,
  }
}
